// ─── ProtectedRoute.js ────────────────────────────────────────────────────────
// Drop-in auth guard for React Router v6.
// Wrap any admin route with this component instead of putting onAuthStateChanged
// inside every page. This is the single source of truth for "is the user logged in?"
//
// USAGE in your App.js / router:
//
//   import ProtectedRoute from './components/ProtectedRoute';
//
//   <Routes>
//     <Route path="/"        element={<DashboardPage />} />
//     <Route path="/login"   element={<LoginPage />} />
//     <Route path="/official" element={
//       <ProtectedRoute>
//         <OfficialPage />
//       </ProtectedRoute>
//     } />
//   </Routes>
//
// If you use ProtectedRoute you can REMOVE the onAuthStateChanged auth guard
// block from OfficialPage.js entirely — this component handles it for you.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { app } from '../firebase/authConfig';

const P = {
  beige: '#F1E6D8',
  muted: '#6B4F3A',
  red:   '#C62828',
  font:  "'DM Sans', 'Segoe UI', sans-serif",
};

export default function ProtectedRoute({ children }) {
  // undefined  = auth hasn't resolved yet (show loading screen)
  // null       = no authenticated user  (redirect to /login)
  // object     = authenticated Firebase user (render children)
  const auth = getAuth(app);
  const [user, setUser] = useState(() => auth.currentUser ?? undefined);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        // Force-refresh the token so the latest custom claims (e.g. admin:true)
        // are reflected immediately — without this, a newly-granted claim only
        // takes effect after the next sign-in.
        try { await firebaseUser.getIdToken(true); } catch (_) {}
      }
      setUser(firebaseUser ?? null);
    });
    return unsubscribe;
  }, [auth]);

  // ── Still waiting for Firebase to resolve the persisted session ──────────
  if (user === undefined) {
    return (
      <div style={{
        minHeight: '100vh',
        background: P.beige,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: P.font,
        gap: 16,
      }}>
        {/* Simple spinner */}
        <div style={{
          width: 36,
          height: 36,
          border: `3px solid #EAE3DA`,
          borderTop: `3px solid ${P.red}`,
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
        }} />
        <div style={{ fontSize: 13, color: P.muted, fontWeight: 500 }}>
          Sinusuri ang session…
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // ── Not authenticated — redirect to login ────────────────────────────────
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // ── Authenticated — render the protected page ────────────────────────────
  return children;
}